import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { filter, finalize, mergeMap, of, take } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { GetAllUAPs, UapLocation } from 'src/app/core/models/admin/adl.model';
import { AdlService } from 'src/app/core/services/admin/adl/adl.service';
import { LocationService } from 'src/app/core/services/admin/location/location.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { TwoColumnFormData } from '../../form/two-column-form/two-column-form.constants';
import { IMAGE_SRC } from '../../shared-constants';
import { BUTTON_TYPES } from '../components/modal-frame/modal-frame.constants';
import { BUTTON_CLICK } from '../exit-modal/exit-modal.constants';
import { CONSTANTS, EditAccessInput } from './edit-access-point.constants';

@Component({
  selector: 'app-edit-access-point',
  templateUrl: './edit-access-point.component.html',
  styleUrls: ['./edit-access-point.component.scss'],
  standalone: false,
})
export class EditAccessPointComponent implements OnInit {
  readonly destroy = inject(DestroyRef);
  data: EditAccessInput = inject(MAT_DIALOG_DATA);
  private dialogRef: MatDialogRef<EditAccessPointComponent> =
    inject(MatDialogRef);
  private adlService: AdlService = inject(AdlService);
  private commonUtils: CommonUtils = inject(CommonUtils);
  private locationService: LocationService = inject(LocationService);

  readonly DISPLAY_CONSTANTS = CONSTANTS;
  readonly IMAGES = IMAGE_SRC;
  readonly BUTTON_TYPES = BUTTON_TYPES;

  availableRoutesData!: TwoColumnFormData[];
  assignedRoutesData!: TwoColumnFormData[];
  totalRoutesData!: TwoColumnFormData[];
  submitButtonVisible = false;

  touched = false;
  location!: string;

  ngOnInit(): void {
    this.locationService
      .getCurrentLocation$()
      .pipe(
        take(1),
        mergeMap((value) => {
          if (value.location) {
            this.location = value.location.locationCode;
            return this.adlService
              .getUAPLocations(value.location.locationCode)
              .pipe(take(1));
          } else {
            return of(false);
          }
        }),
        filter((value) => !!value),
      )
      .subscribe((availableRoutes: GetAllUAPs | boolean) => {
        //Filter out invalid access points for assigned and available values
        const accessPoints = this.commonUtils.filterOutInvalidAccessPoints(
          (availableRoutes as GetAllUAPs).response.uapLocations,
        );
        const assignedPoints = this.commonUtils.filterOutInvalidAccessPoints(
          this.data.data,
        );
        this.assignedRoutesData = (assignedPoints as UapLocation[])
          .map((value: UapLocation) => {
            return { data: value, selected: false };
          })
          .sort((a, b) => a.data.name.localeCompare(b.data.name));
        this.availableRoutesData = (accessPoints as UapLocation[])
          .map((value) => {
            return { data: value, selected: false };
          })
          .sort((a, b) => a.data.name.localeCompare(b.data.name));
        this.filterAssignedData();
      });
  }

  onSubmit(): void {
    const body = this.totalRoutesData.map((route: TwoColumnFormData) => {
      return {
        uapPublicId: route.data.uapPublicId.toString(),
        route: this.data.name,
        name: route.data.name,
        assign: route.selected,
      };
    });

    //Filter out unchanged values
    this.data.data.forEach((item) => {
      const index = body.findIndex(
        (value) => item.uapPublicId === value.uapPublicId && value.assign,
      );
      if (index > -1) {
        body.splice(index, 1);
      }
    });

    this.adlService
      .editAdls(body, this.location)
      .pipe(
        take(1),
        finalize(() => {
          this.dialogRef.close(BUTTON_CLICK.ACCEPT);
          this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
        }),
      )
      .subscribe();
  }

  onClose(): void {
    this.commonUtils.openExitModal(this.touched);
  }

  handleUpdateSubmit($event: boolean) {
    this.submitButtonVisible = $event;
  }

  handleUpdateAssignedValues($event: TwoColumnFormData[]) {
    this.touched = true;
    this.totalRoutesData = $event;
  }

  private filterAssignedData() {
    //Filter out any available options that are already assigned
    this.availableRoutesData = this.availableRoutesData
      .filter((value) => {
        return (
          this.assignedRoutesData.findIndex((route) => {
            return route.data.uapPublicId === value.data.uapPublicId;
          }) === -1
        );
      })
      .sort((a, b) => a.data.name.localeCompare(b.data.name));
    this.assignedRoutesData = this.assignedRoutesData
      .filter((value) => !!value.data.uapPublicId)
      .sort((a, b) => a.data.name.localeCompare(b.data.name));
  }
}
